import { useContext, useState } from 'react';

import { Form, Formik } from 'formik';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';

import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';

import { SubmitButton } from '../../../components/buttons';
import { TextField } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Defaults } from '../../../constants';
import { useGetCategoriesByPage } from '../../../lib/category/hooks';
import { useAddAdditionalParent } from '../../../lib/sub-category/hooks/useAddAdditionalParent';
import { Category } from '../../../types/Category';

type AddParentCategoyButtonProps = {
  subCategoryId: string;
};

function AddParentCategoyButton({ subCategoryId }: AddParentCategoyButtonProps) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);

  const [searchCategory, setSearchCategory] = useState('');
  const debouncedSearchCategoryFn = useDebouncedCallback((value: string) => {
    setSearchCategory(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const { mutateAsync: addAdditionalParent } = useAddAdditionalParent();

  const { data: categories } = useGetCategoriesByPage({
    page: 0,
    sort: '',
    search: searchCategory,
  });

  const handleSubmit = async (values: SelectCategoryFormType) => {
    await addAdditionalParent(
      { subCategoryId, parentCategoryId: values.categoryId! },
      {
        onSuccess: () => {
          handleClose();
          setAlertSnackbar({
            message: 'Additional Parent Category added',
            severity: 'success',
            vertical: 'top',
          });
        },
        onError: (error: any) => {
          setAlertSnackbar({
            message: `Error adding Additional Parent Category: ${error.message || 'Unknown error'}`,
            severity: 'error',
            vertical: 'top',
          });
        },
      },
    );
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        Add
      </Button>

      <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <Formik
          initialValues={{
            categoryId: undefined,
          }}
          validationSchema={SubCategoryEditSchema}
          validateOnBlur={true}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => (
            <Form>
              <DialogTitle id="alert-dialog-title">Select Parent Category</DialogTitle>
              <DialogContent>
                <Autocomplete<Category>
                  style={{ minWidth: '320px', paddingTop: '1rem' }}
                  options={categories || []}
                  getOptionLabel={(option) => option.title}
                  value={categories?.find((c) => c.id === values.categoryId) || null}
                  onChange={(_, value) => {
                    setFieldValue('categoryId', value ? value.id : '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={touched.categoryId && !!errors.categoryId}
                      type="text"
                      name="categoryId"
                      label="Category"
                      onChange={(e) => {
                        handleChange(e);
                        debouncedSearchCategoryFn(e.target.value);
                      }}
                      onBlur={handleBlur}
                      value={values.categoryId}
                      helperText={touched.categoryId && errors.categoryId}
                    />
                  )}
                />
              </DialogContent>
              <StyledDialogActions>
                <Button disabled={false} onClick={handleClose}>
                  Cancel
                </Button>
                <SubmitButton loading={false} disabled={false} autoFocus variant="contained" type="submit">
                  Submit
                </SubmitButton>
              </StyledDialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
}

export default AddParentCategoyButton;

type SelectCategoryFormType = {
  categoryId?: string;
};

const SubCategoryEditSchema = Yup.object().shape({
  categoryId: Yup.string().required('Category is required'),
});

const StyledDialogActions = styled(DialogActions)({
  padding: '1rem',
});
